import type { TableRow } from '../../template/Table';
import { emptyCell, normalizeTable, sanitizeCellHtml } from './tableModel';
import { newContentId } from '../../../services/TemplateUtils';

/** File types the Table block will import (drag-drop or picker). */
export const IMPORT_ACCEPT =
  '.csv,.tsv,.txt,.xlsx,.xls,.docx,.pdf,image/png,image/jpeg,image/webp,image/gif,image/heic';

export type ImportKind = 'delimited' | 'sheet' | 'doc' | 'image';

const KIND_BY_EXT: Record<string, ImportKind> = {
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  webp: 'image',
  gif: 'image',
  heic: 'image',
  heif: 'image',
  pdf: 'doc',
  docx: 'doc',
  doc: 'doc',
  xlsx: 'sheet',
  xls: 'sheet',
  csv: 'delimited',
  tsv: 'delimited',
  txt: 'delimited',
};

/** The first image in a paste/drop `DataTransfer`, or null. */
export function clipboardImageFile(data: DataTransfer | null): File | null {
  if (!data) return null;
  const direct = Array.from(data.files ?? []).find((f) => f.type.startsWith('image/'));
  if (direct) return direct;
  const item = Array.from(data.items ?? []).find(
    (i) => i.kind === 'file' && i.type.startsWith('image/'),
  );
  return item?.getAsFile() ?? null;
}

export function classifyFile(file: File): ImportKind | null {
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  if (KIND_BY_EXT[ext]) return KIND_BY_EXT[ext];
  const type = file.type.toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type === 'application/pdf' || type.includes('word')) return 'doc';
  if (type.includes('spreadsheet')) return 'sheet';
  if (type.startsWith('text/')) return 'delimited';
  return null;
}

function cellFrom(value: string) {
  return {
    ...emptyCell(),
    content: { id: newContentId(), content: sanitizeCellHtml(value.trim()) },
  };
}

/** Split one CSV line, honouring double-quoted fields and doubled-quote escapes. */
function splitCsvLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === delimiter) {
      out.push(field);
      field = '';
    } else {
      field += ch;
    }
  }
  out.push(field);
  return out;
}

/** Parse CSV / TSV text into normalized table rows. Multi-line quoted fields are supported. */
export function delimitedToRows(text: string): TableRow[] {
  const clean = (text ?? '').replace(/\r\n?/g, '\n').trimEnd();
  if (!clean) return [];
  const delimiter = clean.includes('\t') && !clean.includes(',') ? '\t' : ',';

  const lines: string[] = [];
  let buffer = '';
  let quotes = 0;
  for (const raw of clean.split('\n')) {
    buffer = buffer ? `${buffer}\n${raw}` : raw;
    quotes += (raw.match(/"/g) ?? []).length;
    if (quotes % 2 === 0) {
      lines.push(buffer);
      buffer = '';
    }
  }
  if (buffer) lines.push(buffer);

  return normalizeTable(
    lines
      .filter((line) => line.length > 0)
      .map((line) => ({ cells: splitCsvLine(line, delimiter).map(cellFrom) })),
  );
}
